import { api } from '@/lib/api';
import type {
  Site,
  SitesFilters,
  SitesResponse,
  V4SitesResponse,
  CreateSiteInput,
  UpdateSiteInput,
  SiteOptions,
  Account,
  ChangelogEntry,
  SiteChangelogResponse,
  AdminUser,
} from './types';

// =============================================================================
// Sites API Functions
// Data source: PostgreSQL via GET /v4/sites
// =============================================================================

/**
 * Get sites with optional filtering, sorting, and pagination
 */
export async function getSites(filters: SitesFilters = {}): Promise<SitesResponse> {
  const queryParams: Record<string, string | number | string[]> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'name',
    sortDirection: filters.sortDirection ?? 1,
  };

  // Add search filter
  if (filters.search) {
    queryParams.search = filters.search;
  }

  // Add accountId filter
  if (filters.accountId) {
    queryParams.accountId = filters.accountId;
  }

  // Add status filter
  if (filters.status) {
    queryParams.status = filters.status;
  }

  // Add tags filter
  if (filters.tags && filters.tags.length > 0) {
    queryParams.tags = JSON.stringify(filters.tags);
  }

  // Add disabled filter
  if (filters.disabled !== undefined) {
    queryParams.disabled = String(filters.disabled);
  }

  // Per-column filters
  if (filters.name) {
    queryParams.name = filters.name;
  }
  if (filters.address) {
    queryParams.address = filters.address;
  }
  if (filters.locations) {
    queryParams.locations = filters.locations;
  }

  const response = await api.get<V4SitesResponse>('/v4/sites', queryParams);

  return {
    sites: response.sites || [],
    pageNumber: response.pageNumber ?? 0,
    pageLength: response.pageLength ?? 25,
    total: response.total ?? 0,
  };
}

/**
 * Get a single site by ID
 */
export async function getSite(siteId: string): Promise<Site> {
  const response = await api.get<{ site: Site }>(`/v4/sites/${siteId}`);
  return response.site;
}

/**
 * Get site with edit options (locations, usergroups)
 * Note: Still uses V3 endpoint for options since locations/usergroups are MongoDB-only
 */
export async function getSiteWithOptions(siteId: string): Promise<{ site: Site; options: SiteOptions }> {
  // Get site from V4
  const site = await getSite(siteId);

  // Get options from V3 (locations/usergroups are not in PostgreSQL yet)
  const options = await getSiteOptions();

  return { site, options };
}

/**
 * Get options for creating a new site
 * Note: Still uses V3 endpoint since locations/usergroups are MongoDB-only
 */
export async function getSiteOptions(): Promise<SiteOptions> {
  const response = await api.get<SiteOptions>('/v4/legacy/sites/new-options');
  return response;
}

/**
 * Create a new site
 */
export async function createSite(data: CreateSiteInput): Promise<Site> {
  const response = await api.post<{ site: Site }>('/v4/sites', data);
  return response.site;
}

/**
 * Update a site
 */
export async function updateSite(siteId: string, data: UpdateSiteInput): Promise<Site> {
  const response = await api.put<{ site: Site }>(`/v4/sites/${siteId}`, data);
  return response.site;
}

/**
 * Delete a site
 */
export async function deleteSite(siteId: string): Promise<void> {
  await api.delete(`/v4/sites/${siteId}`);
}

/**
 * Get changelog for a site
 */
export async function getSiteChangelog(siteId: string): Promise<ChangelogEntry[]> {
  const response = await api.get<SiteChangelogResponse>(`/v4/sites/${siteId}/changelog`);
  return response.changelog || [];
}

/**
 * Get admin users for the given usergroup IDs
 */
export async function getUsergroupAdmins(usergroupIds: string[]): Promise<AdminUser[]> {
  if (usergroupIds.length === 0) return [];
  const response = await api.get<AdminUser[]>('/v4/legacy/v2/users/admins', {
    usergroups: usergroupIds.join(','),
  });
  return response || [];
}

/**
 * Create a location for a site (quick add via V4 dual-write)
 */
export async function createLocationForSite(
  siteId: string,
  accountId: string,
  data: { name: string; roomNumber?: string },
): Promise<{ id: string; name: string }> {
  const response = await api.post<{ location: { id: string; mongoLocationId: string; name: string } }>('/v4/locations', {
    siteId,
    accountId,
    name: data.name,
    roomNumber: data.roomNumber,
  });
  return response.location;
}

// =============================================================================
// Accounts API Functions (for filter dropdown)
// =============================================================================

interface V4AccountsResponse {
  accounts: Account[];
  pageNumber?: number;
  pageLength?: number;
  total?: number;
}

/**
 * Get all accounts for the dropdown filter
 */
export async function getAccounts(): Promise<Account[]> {
  const response = await api.get<V4AccountsResponse>('/v4/accounts', {
    pageLength: 100,
    sortKey: 'name',
    sortDirection: 1,
    status: 'active',
  });

  return response.accounts || [];
}
